// =============================================================================
// ZAKŁADKA „DOKUMENTY” — lista rejestru dokumentów, TYLKO DO ODCZYTU (UD-12a)
//
// Pasek „Odczyt z Receptora” (stan kursora), filtry w adresie, tabela po 50
// wierszy i objaśnienie stanów. Decyzje (który stan ekranu, która plakietka)
// liczą czyste funkcje z `dokumentyObiegu.ts`; tu jest tylko rysowanie,
// zapytania i zegar.
//
// TRZY RZECZY, KTÓRE TEN EKRAN PILNUJE:
//
//  * BEZ PRAWA NIE PYTA. Rola bez podglądu (albo członkostwo nieaktywne)
//    dostałaby od bazy pustą listę; pusta lista na ekranie znaczy „nie ma
//    dokumentów”, czyli nieprawdę. Wtedy nie wychodzi ŻADNE zapytanie.
//  * TIK NIE PSUJE DOBREJ LISTY. Odświeżenie co 30 s, które nie dostało
//    odpowiedzi (wybudzony laptop, timeout PostgREST), zostawia poprzednie
//    wiersze i mówi, z której chwili są — nie zamienia ich w błąd. Zmiana
//    filtra albo strony pokazuje ładowanie i nigdy starych wierszy pod nowymi
//    filtrami.
//  * ODPOWIEDŹ DLA POPRZEDNIEGO PYTANIA NIE LĄDUJE NA EKRANIE. Każde zapytanie
//    niesie klucz (organizacja + filtry); odpowiedź z innym kluczem niż bieżący
//    jest odrzucana.
//
// Filtr, którego ekran nie przyjął (rodzaj spoza listy, zły UUID zakładu,
// zakład przy jednym zakładzie, numer dłuższy niż 64 znaki), schodzi z adresu:
// adres mówiący o filtrze, którego lista nie stosuje, kłamałby przy podesłaniu.
// =============================================================================

import {
  KODY_STANU_DOKUMENTU,
  NAZWY_RODZAJOW,
  NIE_PODAJE,
  OSIE_OBIEGU,
  RODZAJE_DOKUMENTU_MAGAZYNOWEGO,
  STANY_DOKUMENTU,
  type KodStanuDokumentu,
  type OsObiegu,
} from '@insolitum/types';
import {
  Alert,
  Button,
  EmptyState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from '@insolitum/ui';
import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { ZEGAR_MS } from '../devices/AgenciReceptora';
import {
  nazwaZakladu,
  opisPorazekOdczytu,
  zdanieStanu,
  zdaniePowoduNiezlecania,
  type KursorObiegu,
  type PrawaObiegu,
  type ZakladObiegu,
  type ZakladOrganizacji,
} from '../devices/obiegDokumentow';
import {
  listaAgentow,
  listaKursorowObiegu,
  zakladyOrganizacji,
  type AgentReceptora,
} from '../devices/receptorCoreApi';
import { formatujCzasWzgledny } from '../printing/status';

import {
  KLASA_TONU,
  MAKS_DLUGOSC_NUMERU,
  minutWDopelniaczu,
  OKNO_ODCZYTU_MIN,
  ROZMIAR_STRONY,
  STANY_Z_NAJNOWSZYM,
  TYTULY_STANOW,
  ZDANIE_POPRAWNY_WYNIK,
  czySwiezyOdczyt,
  czytajFiltry,
  czytajZaklad,
  dzienOdKursora,
  dzienProdukcji,
  formatujChwile,
  najwczesniejszyDzienOd,
  okresSiegaPrzedOd,
  opisCelu,
  opisMagazynu,
  opisRodzaju,
  pominieteDokumenty,
  saFiltrySzczegolowe,
  stanKontroli,
  stanListy,
  stanPotwierdzenia,
  stanPrzekazania,
  strefaOkresu,
  strefaZakladu,
  zakladFiltra,
  zdanieNajnowszegoDokumentu,
  zdanieNieswiezegoOdczytu,
  zdanieOd,
  zdanieOpoznienia,
  zdaniePominietych,
  type DokumentObiegu,
  type NajnowszyDokument,
  type StanListy,
  type StanZTytulem,
} from './dokumentyObiegu';
import {
  listaDokumentowObiegu,
  liczbaWymagaDzialania,
  najnowszyDokument,
  type StronaDokumentow,
} from './dokumentyObieguApi';
import {
  MAKS_DNI_OKRESU,
  formatujDzien,
  okresDomyslny,
  okresOstatnichDni,
  walidujOkres,
} from './dzienKalendarzowy';
import { useOdswiezanieWidocznejKarty } from './useOdswiezanieWidocznejKarty';

/** Adres ustawień odczytu — zakładka „Integracje” w Aplikacjach i urządzeniach. */
export const ADRES_USTAWIEN_ODCZYTU = '/admin/devices?tab=integracje';

const NAZWY_OSI: Record<OsObiegu, string> = {
  przekazanie: 'Przekazanie',
  potwierdzenie: 'Potwierdzenie w Receptorze',
  kontrola: 'Kontrola w Receptorze',
  wspolny: 'Wspólne',
};

const godzina = (chwila: number) =>
  new Date(chwila).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });

export interface ZakladkaDokumentyProps {
  organizationId: string | undefined;
  prawa: PrawaObiegu;
  /** Licznik przycisku „Odśwież” w nagłówku strony — każda zmiana to ręczne odświeżenie. */
  odswiezenie: number;
  /** Rytm odświeżania (testy podstawiają krótszy albo sterują zegarem). */
  zegarMs?: number;
}

/** Wynik zapytania przypięty do klucza, który go wywołał. */
interface Przypiety<T> {
  klucz: string;
  wynik: T | 'blad';
}

export function ZakladkaDokumenty({
  organizationId,
  prawa,
  odswiezenie,
  zegarMs = ZEGAR_MS,
}: ZakladkaDokumentyProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [teraz, setTeraz] = useState(() => Date.now());
  const mozePytac = prawa.podglad && Boolean(organizationId);

  const [kursory, setKursory] = useState<KursorObiegu[] | 'blad' | null>(null);
  // Wszystkie lokalizacje organizacji ZE STATUSEM (`zakladyOrganizacji`, ta sama funkcja
  // co nazwy kursorów na karcie agenta): dokument zakładu nieaktywnego zachowuje nazwę.
  const [zaklady, setZaklady] = useState<ZakladOrganizacji[] | 'blad' | null>(null);
  const [agenci, setAgenci] = useState<AgentReceptora[] | 'blad' | null>(null);
  const [lista, setLista] = useState<Przypiety<StronaDokumentow> | null>(null);
  const [liczba, setLiczba] = useState<Przypiety<number> | null>(null);
  /** Chwila ostatniej udanej listy i ostatniego nieudanego odświeżenia. */
  const [daneZ, setDaneZ] = useState<number | null>(null);
  const [nieodswiezone, setNieodswiezone] = useState<number | null>(null);
  /** Najnowszy dokument organizacji, przypięty do organizacji (stany 15–16). */
  const [najnowszy, setNajnowszy] = useState<Przypiety<NajnowszyDokument | null> | null>(null);

  // Okres domyślny liczy się w strefie zakładu z filtra, a bez filtra — w strefie
  // jedynego zakładu. Filtr zakładu obowiązuje dopiero przy dwóch zakładach.
  const zakladId = zakladFiltra(czytajZaklad(searchParams), zaklady);
  const strefa = strefaOkresu(zaklady, zakladId);
  const filtry = czytajFiltry(searchParams, okresDomyslny(teraz, strefa));
  const okres = walidujOkres(filtry.od, filtry.doDnia);
  const szczegolowe = { rodzaj: filtry.rodzaj, zakladId, nr: filtry.nr };
  const kluczFiltrow = [szczegolowe.rodzaj, szczegolowe.zakladId, szczegolowe.nr].join('|');
  const kluczListy =
    mozePytac && okres.ok
      ? [
          organizationId,
          okres.od,
          okres.doWylacznie,
          kluczFiltrow,
          filtry.wymagaDzialania,
          filtry.strona,
        ].join('|')
      : null;
  const kluczLiczby =
    mozePytac && okres.ok
      ? [organizationId, okres.od, okres.doWylacznie, kluczFiltrow].join('|')
      : null;

  // Bieżące parametry dla funkcji wołanych z zegara i z odpowiedzi — klucz
  // porównany po powrocie odpowiedzi rozstrzyga, czy wolno ją pokazać.
  const biezace = useRef({
    organizationId,
    mozePytac,
    okres,
    filtry,
    szczegolowe,
    kluczListy,
    kluczLiczby,
  });
  biezace.current = {
    organizationId,
    mozePytac,
    okres,
    filtry,
    szczegolowe,
    kluczListy,
    kluczLiczby,
  };

  const pobierzListe = async (cicho: boolean) => {
    const b = biezace.current;
    if (!b.organizationId || !b.okres.ok || b.kluczListy === null) return;
    const klucz = b.kluczListy;
    const proba = Date.now();
    try {
      const wynik = await listaDokumentowObiegu(
        b.organizationId,
        {
          od: b.okres.od,
          doWylacznie: b.okres.doWylacznie,
          wymagaDzialania: b.filtry.wymagaDzialania,
          ...b.szczegolowe,
        },
        b.filtry.strona
      );
      if (biezace.current.kluczListy !== klucz) return;
      setLista({ klucz, wynik });
      setDaneZ(proba);
      setNieodswiezone(null);
    } catch {
      if (biezace.current.kluczListy !== klucz) return;
      if (!cicho) {
        setLista({ klucz, wynik: 'blad' });
        return;
      }
      // Tik bez odpowiedzi NIE zastępuje dobrej listy błędem.
      setLista((p) =>
        p && p.klucz === klucz && p.wynik !== 'blad' ? p : { klucz, wynik: 'blad' }
      );
      setNieodswiezone(proba);
    }
  };

  const pobierzLiczbe = async (cicho: boolean) => {
    const b = biezace.current;
    if (!b.organizationId || !b.okres.ok || b.kluczLiczby === null) return;
    const klucz = b.kluczLiczby;
    try {
      const wynik = await liczbaWymagaDzialania(b.organizationId, {
        od: b.okres.od,
        doWylacznie: b.okres.doWylacznie,
        ...b.szczegolowe,
      });
      if (biezace.current.kluczLiczby === klucz) setLiczba({ klucz, wynik });
    } catch {
      if (biezace.current.kluczLiczby !== klucz) return;
      setLiczba((p) =>
        cicho && p && p.klucz === klucz && p.wynik !== 'blad' ? p : { klucz, wynik: 'blad' }
      );
    }
  };

  const odswiezKursory = async () => {
    const org = biezace.current.organizationId;
    if (!org || !biezace.current.mozePytac) return;
    try {
      const wynik = await listaKursorowObiegu(org);
      if (biezace.current.organizationId === org) setKursory(wynik);
    } catch {
      if (biezace.current.organizationId === org) {
        setKursory((p) => (Array.isArray(p) ? p : 'blad'));
      }
    }
  };

  const odswiezZaklady = async () => {
    const org = biezace.current.organizationId;
    if (!org || !biezace.current.mozePytac) return;
    try {
      const wynik = await zakladyOrganizacji(org);
      if (biezace.current.organizationId === org) setZaklady(wynik);
    } catch {
      if (biezace.current.organizationId === org) {
        setZaklady((p) => (Array.isArray(p) ? p : 'blad'));
      }
    }
  };

  // Organizacja: kursory i zakłady od nowa — dane poprzedniej nie mają prawa
  // zostać na ekranie nowej.
  useEffect(() => {
    setKursory(null);
    setZaklady(null);
    setAgenci(null);
    setNajnowszy(null);
    setDaneZ(null);
    setNieodswiezone(null);
    if (!mozePytac) return;
    void odswiezKursory();
    void odswiezZaklady();
  }, [organizationId, mozePytac]);

  // Zmiana filtra albo strony: ładowanie (klucz listy się zmienił, więc stara
  // lista już nie pasuje) i nowe zapytanie.
  useEffect(() => {
    if (kluczListy === null) return;
    setNieodswiezone(null);
    void pobierzListe(false);
  }, [kluczListy]);

  useEffect(() => {
    if (kluczLiczby === null) return;
    void pobierzLiczbe(false);
  }, [kluczLiczby]);

  const odswiezWszystko = () => {
    setTeraz(Date.now());
    void odswiezKursory();
    void pobierzListe(true);
    void pobierzLiczbe(true);
  };

  // Ręczne odświeżenie z nagłówka (pierwszy render to nie kliknięcie).
  const poprzednieOdswiezenie = useRef(odswiezenie);
  useEffect(() => {
    if (poprzednieOdswiezenie.current === odswiezenie) return;
    poprzednieOdswiezenie.current = odswiezenie;
    odswiezWszystko();
  }, [odswiezenie]);

  // Tik: zegar ekranu i odświeżenie w tym samym tiku. Na stronach 2+ tylko
  // kursory — nowe dokumenty przesuwałyby wiersze pod ręką.
  useOdswiezanieWidocznejKarty(
    () => {
      setTeraz(Date.now());
      void odswiezKursory();
      if (biezace.current.filtry.strona === 1) {
        void pobierzListe(true);
        void pobierzLiczbe(true);
      }
    },
    zegarMs,
    mozePytac
  );

  const listaBiezaca = lista && lista.klucz === kluczListy ? lista.wynik : null;
  const liczbaBiezaca = liczba && liczba.klucz === kluczLiczby ? liczba.wynik : null;

  const { stan, kursor } = stanListy({
    prawa,
    organizacja: Boolean(organizationId),
    okres,
    lista: listaBiezaca === null || listaBiezaca === 'blad' ? listaBiezaca : listaBiezaca.wiersze,
    kursory,
    zaklady,
    wymagaDzialania: filtry.wymagaDzialania,
    filtrySzczegolowe: saFiltrySzczegolowe(szczegolowe),
    strona: filtry.strona,
    teraz,
  });

  // Agenci — wyłącznie w stanie „nie ma skąd”: od nich zależy, który krok jest
  // następny (zainstalować agenta czy włączyć odczyt).
  const potrzebaAgentow = stan === 'nie_ma_skad';
  useEffect(() => {
    if (!potrzebaAgentow || !organizationId) return;
    const org = organizationId;
    let zywe = true;
    listaAgentow(org).then(
      (wynik) => {
        if (zywe && biezace.current.organizationId === org) setAgenci(wynik);
      },
      () => {
        if (zywe && biezace.current.organizationId === org) setAgenci('blad');
      }
    );
    return () => {
      zywe = false;
    };
  }, [potrzebaAgentow, organizationId]);

  // Najnowszy dokument — WYŁĄCZNIE w stanach 15–16 (zero przy świeżym odczycie):
  // rozdziela „pusto w tym okresie” od „pusto w ogóle”. Pyta raz na wejście w
  // stan; w każdym innym stanie to pytanie niczego by nie rozstrzygnęło.
  const potrzebaNajnowszego = (STANY_Z_NAJNOWSZYM as readonly string[]).includes(stan);
  useEffect(() => {
    if (!potrzebaNajnowszego || !organizationId) return;
    const org = organizationId;
    let zywe = true;
    najnowszyDokument(org).then(
      (wynik) => {
        if (zywe && biezace.current.organizationId === org) setNajnowszy({ klucz: org, wynik });
      },
      () => {
        if (zywe && biezace.current.organizationId === org) {
          setNajnowszy({ klucz: org, wynik: 'blad' });
        }
      }
    );
    return () => {
      zywe = false;
    };
  }, [potrzebaNajnowszego, organizationId]);
  const najnowszyBiezacy =
    najnowszy && najnowszy.klucz === organizationId ? najnowszy.wynik : undefined;

  // Pusta strona 2+ (adres z dawnym numerem strony) — wracamy na stronę 1.
  const pustaDalszaStrona =
    filtry.strona > 1 &&
    listaBiezaca !== null &&
    listaBiezaca !== 'blad' &&
    listaBiezaca.wiersze.length === 0;

  const ustawParametry = (zmiany: Record<string, string | null>, zerujStrone = true) => {
    const nastepne = new URLSearchParams(searchParams);
    for (const [klucz, wartosc] of Object.entries(zmiany)) {
      if (wartosc === null) nastepne.delete(klucz);
      else nastepne.set(klucz, wartosc);
    }
    if (zerujStrone) nastepne.delete('strona');
    setSearchParams(nastepne, { replace: true });
  };

  useEffect(() => {
    if (pustaDalszaStrona) ustawParametry({ strona: null }, false);
  }, [pustaDalszaStrona]);

  // Filtr z adresu, którego ekran nie przyjął, schodzi z adresu.
  const doZdjecia = [
    searchParams.get('rodzaj') !== null && filtry.rodzaj === null ? 'rodzaj' : null,
    searchParams.get('zaklad') !== null && szczegolowe.zakladId === null ? 'zaklad' : null,
    searchParams.get('nr') !== null && filtry.nr === null ? 'nr' : null,
  ].filter((k): k is string => k !== null);
  const kluczZdjecia = doZdjecia.join(',');
  useEffect(() => {
    if (kluczZdjecia === '') return;
    ustawParametry(Object.fromEntries(kluczZdjecia.split(',').map((k) => [k, null])));
  }, [kluczZdjecia]);

  // Pole numeru trzyma to, co człowiek pisze; do adresu (i do zapytania) idzie
  // dopiero po „Szukaj” — zapytanie po każdym znaku zamieniałoby listę w migawkę.
  const [nrPole, setNrPole] = useState(filtry.nr ?? '');
  useEffect(() => {
    setNrPole(filtry.nr ?? '');
  }, [filtry.nr]);

  // „Ostatnie 93 dni” — najdłuższy okres, jaki przyjmuje walidacja, dziś włącznie.
  const ostatnie93 = okresOstatnichDni(teraz, strefa, MAKS_DNI_OKRESU);
  const pokazOstatnie93 =
    okres.ok && okres.od === ostatnie93.od && okres.doDnia === ostatnie93.doDnia
      ? null
      : () => {
          const o = okresOstatnichDni(Date.now(), strefa, MAKS_DNI_OKRESU);
          ustawParametry({ od: o.od, do: o.doDnia });
        };

  const wyczyscFiltry = () => {
    const tab = searchParams.get('tab');
    const nastepne = new URLSearchParams();
    if (tab) nastepne.set('tab', tab);
    setSearchParams(nastepne, { replace: true });
  };

  // ── Rysowanie ──────────────────────────────────────────────────────────────

  if (stan === 'brak_prawa' || stan === 'bez_organizacji') {
    return (
      <div data-testid="zakladka-dokumenty" data-stan={stan}>
        <StanPusty stan={stan} />
      </div>
    );
  }

  const zakladyLista = Array.isArray(zaklady) ? zaklady : [];
  const wieleZakladow = zakladyLista.length >= 2;
  const linkUstawien = (
    <Link to={ADRES_USTAWIEN_ODCZYTU} className="text-aurora-brand underline">
      Aplikacje i urządzenia › Integracje
    </Link>
  );
  const ostatniOdczyt = (k: KursorObiegu, zWzglednym: boolean) =>
    k.ostatni_odczyt_at
      ? `${formatujChwile(k.ostatni_odczyt_at, strefaZakladu(zaklady, k.zaklad_id))}${
          zWzglednym ? ` (${formatujCzasWzgledny(k.ostatni_odczyt_at, teraz)})` : ''
        }`
      : 'jeszcze nie było';
  const tekstLiczby =
    liczbaBiezaca === null ? '…' : liczbaBiezaca === 'blad' ? '?' : String(liczbaBiezaca);

  return (
    <div className="space-y-4" data-testid="zakladka-dokumenty" data-stan={stan}>
      <PasekOdczytu
        kursory={kursory}
        zaklady={zaklady}
        teraz={teraz}
        zOstrzezeniami={stan === 'lista'}
        okres={okres}
        linkUstawien={linkUstawien}
        ostatniOdczyt={ostatniOdczyt}
      />
      {stan === 'lista' && kursory === 'blad' && (
        <Alert variant="warning">
          Nie udało się sprawdzić stanu odczytu z Receptora — lista może być niepełna.
        </Alert>
      )}
      {stan === 'lista' && nieodswiezone !== null && daneZ !== null && (
        <p className="text-xs text-aurora-warning" data-testid="nieodswiezone">
          Nie udało się odświeżyć ({godzina(nieodswiezone)}) — pokazujemy dane z {godzina(daneZ)}.
        </p>
      )}

      {/* Filtry — wszystkie w adresie, więc link „zobacz to” da się komuś podesłać. */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="text-xs text-aurora-muted">
          Od dnia
          <input
            type="date"
            className="input mt-1 block"
            value={filtry.od}
            aria-invalid={!okres.ok}
            onChange={(e) => ustawParametry({ od: e.target.value })}
          />
        </label>
        <label className="text-xs text-aurora-muted">
          Do dnia
          <input
            type="date"
            className="input mt-1 block"
            value={filtry.doDnia}
            aria-invalid={!okres.ok}
            onChange={(e) => ustawParametry({ do: e.target.value })}
          />
        </label>
        <label className="text-xs text-aurora-muted max-w-full">
          Rodzaj
          <select
            className="select mt-1 block max-w-full"
            value={filtry.rodzaj ?? ''}
            onChange={(e) => ustawParametry({ rodzaj: e.target.value || null })}
          >
            <option value="">Wszystkie rodzaje</option>
            {RODZAJE_DOKUMENTU_MAGAZYNOWEGO.map((r) => (
              <option key={r} value={r}>
                {NAZWY_RODZAJOW[r].skrot} · {NAZWY_RODZAJOW[r].nazwa}
              </option>
            ))}
          </select>
        </label>
        {/* Zakład — dopiero przy dwóch: przy jednym filtr nic by nie zawęził. */}
        {wieleZakladow && (
          <label className="text-xs text-aurora-muted max-w-full">
            Zakład
            <select
              className="select mt-1 block max-w-full"
              value={szczegolowe.zakladId ?? ''}
              onChange={(e) => ustawParametry({ zaklad: e.target.value || null })}
            >
              <option value="">Wszystkie zakłady</option>
              {zakladyLista.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.name}
                </option>
              ))}
            </select>
          </label>
        )}
        <form
          className="flex items-end gap-2"
          role="search"
          onSubmit={(e) => {
            e.preventDefault();
            ustawParametry({ nr: nrPole.trim() || null });
          }}
        >
          <label className="text-xs text-aurora-muted">
            Numer dokumentu
            <input
              type="search"
              className="input mt-1 block"
              value={nrPole}
              maxLength={MAKS_DLUGOSC_NUMERU}
              placeholder="np. PW/2026/10"
              onChange={(e) => setNrPole(e.target.value)}
            />
          </label>
          <Button type="submit" variant="secondary" size="sm">
            Szukaj
          </Button>
        </form>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="chip"
            aria-pressed={!filtry.wymagaDzialania}
            onClick={() => ustawParametry({ grupa: null })}
          >
            Wszystkie
          </button>
          <button
            type="button"
            className="chip"
            aria-pressed={filtry.wymagaDzialania}
            onClick={() => ustawParametry({ grupa: 'wymaga_dzialania' })}
            data-testid="chip-wymaga-dzialania"
          >
            Wymaga działania · {tekstLiczby}
          </button>
        </div>
        <Button variant="ghost" size="sm" onClick={wyczyscFiltry}>
          Wyczyść filtry
        </Button>
      </div>
      {!okres.ok && (
        <p className="text-xs text-aurora-danger" data-testid="powod-okresu">
          {okres.powod}
        </p>
      )}
      {/* Numer szukamy w oknie okresu — zdanie mówi, w którym, a przycisk je poszerza. */}
      {filtry.nr !== null && okres.ok && (
        <div
          className="flex flex-wrap items-center gap-2 text-xs text-aurora-muted"
          data-testid="zakres-szukania"
        >
          <span>
            Szukamy w okresie {formatujDzien(okres.od)}–{formatujDzien(okres.doDnia)}.
          </span>
          {pokazOstatnie93 && (
            <Button variant="ghost" size="sm" onClick={pokazOstatnie93}>
              Szukaj w ostatnich {MAKS_DNI_OKRESU} dniach
            </Button>
          )}
        </div>
      )}

      {(stan === 'lista' || stan === 'ladowanie') && (
        <TabelaDokumentow
          wiersze={
            stan === 'lista' && listaBiezaca && listaBiezaca !== 'blad'
              ? listaBiezaca.wiersze
              : null
          }
          wieleZakladow={wieleZakladow}
          zaklady={zaklady}
          teraz={teraz}
          onOdswiez={odswiezWszystko}
        />
      )}

      {stan === 'lista' && listaBiezaca && listaBiezaca !== 'blad' && (
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-aurora-muted">
          <span data-testid="podpis-strony">
            Dokumenty {(filtry.strona - 1) * ROZMIAR_STRONY + 1}–
            {(filtry.strona - 1) * ROZMIAR_STRONY + listaBiezaca.wiersze.length}
          </span>
          <div className="flex gap-2">
            {filtry.strona > 1 && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => ustawParametry({ strona: String(filtry.strona - 1) }, false)}
              >
                Nowsze
              </Button>
            )}
            {listaBiezaca.jestNastepna && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => ustawParametry({ strona: String(filtry.strona + 1) }, false)}
              >
                Starsze
              </Button>
            )}
          </div>
        </div>
      )}

      {stan === 'blad_odczytu' && (
        <div data-stan="blad_odczytu">
          <Alert variant="error" title={TYTULY_STANOW.blad_odczytu}>
            <p>To nie znaczy, że ich nie ma.</p>
            <Button variant="secondary" size="sm" className="mt-2" onClick={odswiezWszystko}>
              <RefreshCw className="w-4 h-4" />
              Odśwież
            </Button>
          </Alert>
        </div>
      )}

      {stan !== 'lista' && stan !== 'ladowanie' && stan !== 'blad_odczytu' && (
        <StanPusty
          stan={stan}
          opis={opisStanuPustego({
            stan,
            kursor,
            kursory,
            zaklady,
            agenci,
            okres,
            teraz,
            ostatniOdczyt,
            powodOkresu: okres.ok ? null : okres.powod,
            najnowszy: najnowszyBiezacy,
          })}
          dzialanie={dzialanieStanuPustego({
            stan,
            kursory,
            zaklady,
            linkUstawien,
            onOdswiez: odswiezWszystko,
            onWyczysc: wyczyscFiltry,
            onPokazWszystkie: () => ustawParametry({ grupa: null }),
            onPokazOd: (dzien) => ustawParametry({ od: dzien, do: null }),
            onPokazOstatnie93: pokazOstatnie93,
          })}
        />
      )}

      <ObjasnienieStanow />
    </div>
  );
}

// ── Stany puste ──────────────────────────────────────────────────────────────

function StanPusty({
  stan,
  opis,
  dzialanie,
}: {
  stan: StanZTytulem;
  opis?: string;
  dzialanie?: ReactNode;
}) {
  return (
    <EmptyState
      variant="card"
      data-stan={stan}
      title={TYTULY_STANOW[stan]}
      description={opis}
      action={dzialanie}
    />
  );
}

interface WejscieOpisu {
  stan: StanListy;
  kursor: KursorObiegu | null;
  kursory: KursorObiegu[] | 'blad' | null;
  zaklady: ZakladObiegu[] | 'blad' | null;
  agenci: AgentReceptora[] | 'blad' | null;
  okres: ReturnType<typeof walidujOkres>;
  teraz: number;
  ostatniOdczyt: (k: KursorObiegu, zWzglednym: boolean) => string;
  powodOkresu: string | null;
  /** Najnowszy dokument organizacji (stany 15–16); `undefined` = pytanie w toku. */
  najnowszy: NajnowszyDokument | null | 'blad' | undefined;
}

/** Treść stanu pustego — zdania z §3.9 planu, każde tylko wtedy, gdy dotyczy. */
function opisStanuPustego(w: WejscieOpisu): string | undefined {
  const k = w.kursor;
  const najnowszy = zdanieNajnowszegoDokumentu(w.najnowszy);
  switch (w.stan) {
    case 'okres_niepoprawny':
      return w.powodOkresu ?? undefined;
    case 'pusto_stan_nieznany':
      return 'Nie udało się sprawdzić, czy odczyt z Receptora działa.';
    case 'nie_ma_skad': {
      if (w.agenci === 'blad') {
        return 'Odczyt dokumentów z Receptora nie jest włączony. Włącza go właściciel albo administrator przy agencie integracji. Przed włączeniem ustal z opiekunem Insolitum chwilę startu: dokumentów założonych w Receptorze wcześniej Universe nie odczyta.';
      }
      const zywy = Array.isArray(w.agenci)
        ? w.agenci.find((a) => a.status === 'active')
        : undefined;
      if (Array.isArray(w.agenci) && !zywy) {
        return 'Ta firma nie ma połączonego Receptora. Zainstaluj agenta integracji na serwerze z Receptorem i sparuj go.';
      }
      if (!zywy) return undefined;
      return `Odczyt dokumentów z Receptora nie jest włączony. Włącza go właściciel albo administrator przy agencie ${zywy.name}. Przed włączeniem ustal z opiekunem Insolitum chwilę startu: dokumentów założonych w Receptorze wcześniej Universe nie odczyta.`;
    }
    case 'przed_startem':
      return k ? zdanieOd(k, w.zaklady) : undefined;
    case 'zatrzymany':
      return k ? zdanieStanu(k.stan) : undefined;
    case 'nie_zlecany':
      return k
        ? `${zdaniePowoduNiezlecania(k.harmonogram_powod) ?? ''} Ostatni udany odczyt: ${w.ostatniOdczyt(
            k,
            true
          )}.`
        : undefined;
    case 'nie_czytano':
      return `Odczyt jest włączony, a pierwszy rusza sam w ciągu ${minutWDopelniaczu(OKNO_ODCZYTU_MIN)}. Ta lista odświeża się sama co ${Math.round(
        ZEGAR_MS / 1000
      )} sekund.`;
    case 'odczyt_niepewny': {
      if (!k) return undefined;
      const dodatek =
        k.stan !== 'aktywny'
          ? zdanieStanu(k.stan)
          : k.porazki_z_rzedu > 0
            ? `${opisPorazekOdczytu(k, w.teraz)}.`
            : zdanieNieswiezegoOdczytu(k, w.teraz);
      return `Ostatni udany odczyt z Receptora: ${w.ostatniOdczyt(k, true)}. ${dodatek}`;
    }
    case 'nic_do_dzialania':
      return k ? `Ostatni odczyt z Receptora: ${w.ostatniOdczyt(k, false)}.` : undefined;
    case 'zero_po_filtrach': {
      if (!k || !w.okres.ok) return undefined;
      const zdania = [
        `Szukaliśmy w okresie ${formatujDzien(w.okres.od)}–${formatujDzien(w.okres.doDnia)}.`,
        `Ostatni odczyt z Receptora: ${w.ostatniOdczyt(k, false)}.`,
      ];
      if (najnowszy) zdania.push(najnowszy);
      return zdania.join(' ');
    }
    case 'zero_w_okresie': {
      if (!k) return undefined;
      const zdania = [`Ostatni odczyt z Receptora: ${w.ostatniOdczyt(k, true)}.`];
      if (najnowszy) zdania.push(najnowszy);
      const przedOd = okresSiegaPrzedOd(w.okres, w.kursory, w.zaklady);
      const pominiete = Array.isArray(w.kursory)
        ? w.kursory.reduce((suma, x) => suma + pominieteDokumenty(x), 0)
        : 0;
      if (przedOd) zdania.push(zdanieOd(przedOd, w.zaklady));
      if (pominiete > 0) zdania.push(zdaniePominietych(pominiete));
      if (!przedOd && pominiete === 0) zdania.push(ZDANIE_POPRAWNY_WYNIK);
      return zdania.join(' ');
    }
    default:
      return undefined;
  }
}

function dzialanieStanuPustego(w: {
  stan: StanListy;
  kursory: KursorObiegu[] | 'blad' | null;
  zaklady: ZakladObiegu[] | 'blad' | null;
  linkUstawien: ReactNode;
  onOdswiez: () => void;
  onWyczysc: () => void;
  onPokazWszystkie: () => void;
  onPokazOd: (dzien: string) => void;
  /** `null` = okres już jest ostatnimi 93 dniami, przycisk nic by nie zmienił. */
  onPokazOstatnie93: (() => void) | null;
}): ReactNode {
  const ostatnie93 = w.onPokazOstatnie93 && (
    <Button key="93" variant="secondary" size="sm" onClick={w.onPokazOstatnie93}>
      Pokaż ostatnie {MAKS_DNI_OKRESU} dni
    </Button>
  );
  const obok = (...przyciski: ReactNode[]) => (
    <div className="flex flex-wrap justify-center gap-2">{przyciski}</div>
  );
  switch (w.stan) {
    case 'okres_niepoprawny':
      return (
        <Button variant="secondary" size="sm" onClick={w.onWyczysc}>
          Wyczyść filtry
        </Button>
      );
    case 'zero_po_filtrach':
      return obok(
        <Button key="wyczysc" variant="secondary" size="sm" onClick={w.onWyczysc}>
          Wyczyść filtry
        </Button>,
        ostatnie93
      );
    case 'zero_w_okresie':
      return ostatnie93 || null;
    case 'pusto_stan_nieznany':
      return (
        <Button variant="secondary" size="sm" onClick={w.onOdswiez}>
          <RefreshCw className="w-4 h-4" />
          Odśwież
        </Button>
      );
    case 'nie_ma_skad':
    case 'zatrzymany':
    case 'nie_zlecany':
    case 'odczyt_niepewny':
      return <span className="text-sm">{w.linkUstawien}</span>;
    case 'przed_startem': {
      const dzien = Array.isArray(w.kursory) ? najwczesniejszyDzienOd(w.kursory, w.zaklady) : null;
      return dzien ? (
        <Button variant="secondary" size="sm" onClick={() => w.onPokazOd(dzien)}>
          Pokaż od {formatujDzien(dzien)}
        </Button>
      ) : null;
    }
    case 'nic_do_dzialania':
      return obok(
        <Button key="wszystkie" variant="secondary" size="sm" onClick={w.onPokazWszystkie}>
          Pokaż wszystkie
        </Button>,
        ostatnie93
      );
    default:
      return null;
  }
}

// ── Pasek „Odczyt z Receptora” ───────────────────────────────────────────────

function PasekOdczytu({
  kursory,
  zaklady,
  teraz,
  zOstrzezeniami,
  okres,
  linkUstawien,
  ostatniOdczyt,
}: {
  kursory: KursorObiegu[] | 'blad' | null;
  zaklady: ZakladObiegu[] | 'blad' | null;
  teraz: number;
  zOstrzezeniami: boolean;
  okres: ReturnType<typeof walidujOkres>;
  linkUstawien: ReactNode;
  ostatniOdczyt: (k: KursorObiegu, zWzglednym: boolean) => string;
}) {
  if (kursory === null) return <div className="skel h-12" aria-hidden="true" />;
  if (!Array.isArray(kursory) || kursory.length === 0) return null;
  return (
    <div className="card p-4 space-y-2 text-sm" data-testid="pasek-odczytu">
      {kursory.map((k) => {
        const swiezy = czySwiezyOdczyt(k, teraz);
        const powod = zdaniePowoduNiezlecania(k.harmonogram_powod);
        const pominiete = pominieteDokumenty(k);
        const przedOd = okres.ok && okres.od < dzienOdKursora(k, zaklady);
        return (
          <div key={k.id} className="space-y-1" data-testid={`kursor-${k.id}`}>
            <p className="text-aurora-text">
              Odczyt z Receptora · {nazwaZakladu(zaklady, k.zaklad_id)} · dokumenty założone od{' '}
              {formatujChwile(k.od, strefaZakladu(zaklady, k.zaklad_id))} · ostatni odczyt{' '}
              {ostatniOdczyt(k, true)}
            </p>
            <p className="text-xs text-aurora-subtle">{zdanieOpoznienia(k)}</p>
            {zOstrzezeniami && (
              <>
                {k.stan !== 'aktywny' && <Alert variant="warning">{zdanieStanu(k.stan)}</Alert>}
                {powod && (
                  <p className="text-xs text-aurora-warning">
                    Odczyt nie jest teraz zlecany: {powod}
                  </p>
                )}
                {k.porazki_z_rzedu > 0 && (
                  <p className="text-xs text-aurora-warning">{opisPorazekOdczytu(k, teraz)}</p>
                )}
                {k.stan === 'aktywny' &&
                  !powod &&
                  k.porazki_z_rzedu === 0 &&
                  k.ostatni_odczyt_at &&
                  !swiezy && (
                    <p className="text-xs text-aurora-warning" data-testid="odczyt-nieswiezy">
                      {zdanieNieswiezegoOdczytu(k, teraz)}
                    </p>
                  )}
                {pominiete > 0 && (
                  <p className="text-xs text-aurora-warning">{zdaniePominietych(pominiete)}</p>
                )}
                {przedOd && <p className="text-xs text-aurora-muted">{zdanieOd(k, zaklady)}</p>}
              </>
            )}
          </div>
        );
      })}
      <p className="text-xs text-aurora-muted">Ustawienia odczytu: {linkUstawien}</p>
    </div>
  );
}

// ── Tabela ───────────────────────────────────────────────────────────────────

function Plakietka({
  kod,
  zeZdaniem = true,
  onOdswiez,
}: {
  kod: KodStanuDokumentu;
  zeZdaniem?: boolean;
  onOdswiez?: () => void;
}) {
  const opis = STANY_DOKUMENTU[kod];
  const uwaga = opis.ton === 'error' || opis.ton === 'trial';
  return (
    <div className="space-y-1">
      <span className={KLASA_TONU[opis.ton]} data-kod={kod}>
        <span className="pin" aria-hidden="true" />
        {opis.glif && <span aria-hidden="true">{opis.glif}</span>}
        {opis.etykieta}
      </span>
      {uwaga && zeZdaniem && <p className="text-xs text-aurora-muted">{opis.zdanie}</p>}
      {opis.czynnosc === 'odswiez' && onOdswiez && (
        <Button variant="ghost" size="sm" aria-label="Odśwież listę dokumentów" onClick={onOdswiez}>
          <RefreshCw className="w-4 h-4" />
          Odśwież
        </Button>
      )}
    </div>
  );
}

const KOLUMNY_TABELI = 8;

function TabelaDokumentow({
  wiersze,
  wieleZakladow,
  zaklady,
  teraz,
  onOdswiez,
}: {
  /** `null` = ładowanie. */
  wiersze: DokumentObiegu[] | null;
  wieleZakladow: boolean;
  zaklady: ZakladObiegu[] | 'blad' | null;
  teraz: number;
  onOdswiez: () => void;
}) {
  return (
    <div className="card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Dokument</TableHead>
            <TableHead className="hidden md:table-cell">Cel</TableHead>
            <TableHead>Data operacji</TableHead>
            <TableHead className="hidden md:table-cell">Dzień produkcji</TableHead>
            <TableHead className="hidden md:table-cell">Magazyn</TableHead>
            <TableHead className="hidden md:table-cell text-right">Pozycje</TableHead>
            {wieleZakladow && <TableHead className="hidden md:table-cell">Zakład</TableHead>}
            <TableHead>Receptor</TableHead>
            <TableHead className="hidden md:table-cell">Przekazanie</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {wiersze === null ? (
            <TableLoading colSpan={KOLUMNY_TABELI + (wieleZakladow ? 1 : 0)} />
          ) : (
            wiersze.map((d) => (
              <WierszDokumentu
                key={d.id}
                d={d}
                wieleZakladow={wieleZakladow}
                zaklady={zaklady}
                teraz={teraz}
                onOdswiez={onOdswiez}
              />
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function WierszDokumentu({
  d,
  wieleZakladow,
  zaklady,
  teraz,
  onOdswiez,
}: {
  d: DokumentObiegu;
  wieleZakladow: boolean;
  zaklady: ZakladObiegu[] | 'blad' | null;
  teraz: number;
  onOdswiez: () => void;
}) {
  const przekazanie = stanPrzekazania(d);
  const potwierdzenie = stanPotwierdzenia(d, teraz);
  const kontrola = stanKontroli(d, teraz);
  const rodzaj = opisRodzaju(d);
  const dzien = dzienProdukcji(d);
  const magazyn = opisMagazynu(d);
  // Jeden przycisk na wiersz: przy „Nie podaje” w przekazaniu, a gdy tam go nie
  // ma — przy „Nie podaje” w potwierdzeniu.
  const przyciskWPrzekazaniu = przekazanie === 'nie_podaje';
  const numer = d.numer_zrodlowy?.trim();

  return (
    <TableRow data-testid={`dokument-${d.id}`}>
      <TableCell>
        <div className={numer ? 'mono font-semibold text-aurora-text' : 'text-aurora-subtle'}>
          {numer || 'bez numeru'}
        </div>
        <div className={rodzaj.znany ? 'text-xs' : 'text-xs text-[color:var(--ids-tl-nieznane)]'}>
          {rodzaj.tekst}
        </div>
        <div className="md:hidden mt-1">
          <Plakietka kod={przekazanie} />
        </div>
      </TableCell>
      <TableCell className="hidden md:table-cell">{opisCelu(d.cel)}</TableCell>
      <TableCell className="mono">{formatujDzien(d.data_operacji)}</TableCell>
      <TableCell className="hidden md:table-cell">
        {typeof dzien === 'object' ? (
          <span className="mono">{formatujDzien(dzien.wartosc)}</span>
        ) : dzien === 'nie_podaje' ? (
          <span className="text-[color:var(--ids-tl-nieznane)]">? {NIE_PODAJE}</span>
        ) : (
          <span className="text-aurora-subtle">nie dotyczy</span>
        )}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <span className={magazyn === NIE_PODAJE ? 'text-[color:var(--ids-tl-nieznane)]' : ''}>
          {magazyn}
        </span>
      </TableCell>
      <TableCell className="hidden md:table-cell mono text-right">{d.liczba_pozycji}</TableCell>
      {wieleZakladow && (
        <TableCell className="hidden md:table-cell">
          {d.zaklad_id ? nazwaZakladu(zaklady, d.zaklad_id) : NIE_PODAJE}
        </TableCell>
      )}
      <TableCell>
        <Plakietka
          kod={potwierdzenie.kod}
          onOdswiez={przyciskWPrzekazaniu ? undefined : onOdswiez}
        />
        {potwierdzenie.surowy && (
          <div className="text-xs mono text-aurora-subtle">kod: {potwierdzenie.surowy}</div>
        )}
        {kontrola && (
          <div
            className={
              kontrola.kod === 'kontrola_nie_nadaza'
                ? 'text-xs text-aurora-warning'
                : 'text-xs text-aurora-subtle'
            }
            data-kod={kontrola.kod}
            title={STANY_DOKUMENTU[kontrola.kod].zdanie}
          >
            {kontrola.kod === 'kontrola_biezaca'
              ? `sprawdzony ${formatujCzasWzgledny(kontrola.sprawdzonoAt, teraz)}`
              : kontrola.kod === 'kontrola_nie_nadaza'
                ? 'kontrola nie nadąża'
                : kontrola.kod === 'kontrola_poza_oknem'
                  ? 'poza kontrolą'
                  : `kontrola: ${NIE_PODAJE}`}
          </div>
        )}
      </TableCell>
      <TableCell className="hidden md:table-cell">
        <Plakietka kod={przekazanie} onOdswiez={przyciskWPrzekazaniu ? onOdswiez : undefined} />
      </TableCell>
    </TableRow>
  );
}

// ── Objaśnienie stanów ───────────────────────────────────────────────────────

/**
 * Czytelnik KAŻDEGO kodu kartoteki — zamiast panelu szczegółów, który należy do
 * UD-12. Kolejność osi i kodów jest kolejnością kartoteki.
 */
function ObjasnienieStanow() {
  return (
    <details className="card p-4 text-sm" data-testid="objasnienie-stanow">
      <summary className="cursor-pointer font-semibold text-aurora-text">
        Co znaczą stany dokumentów
      </summary>
      <div className="mt-3 space-y-4">
        {OSIE_OBIEGU.map((os) => (
          <section key={os}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-aurora-subtle">
              {NAZWY_OSI[os]}
            </h4>
            <dl className="mt-2 space-y-2">
              {KODY_STANU_DOKUMENTU.filter((kod) => STANY_DOKUMENTU[kod].os === os).map((kod) => {
                const opis = STANY_DOKUMENTU[kod];
                return (
                  <div key={kod} data-kod={kod}>
                    <dt>
                      <span className={KLASA_TONU[opis.ton]}>
                        <span className="pin" aria-hidden="true" />
                        {opis.glif && <span aria-hidden="true">{opis.glif}</span>}
                        {opis.etykieta}
                      </span>
                    </dt>
                    <dd className="mt-1 text-xs text-aurora-muted">{opis.zdanie}</dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>
    </details>
  );
}
